<template>
  <app-page
    title="Rewards settings"
    subtitle="Use this balance to fund employee rewards and recognition."
    :previous="[{ label: 'Recognition', to: { name: 'recognition' } }]"
    back-route="/recognition"
  >
    <template v-slot:header-content>
      <app-stat
        :stat="ledger?.balance_minor ?? 0"
        :formatter="getCurrencyFormatter(ledger?.balance_minor ?? 0)"
        title="Balance"
        color="default"
        small
        class="header-stat"
      />
    </template>

    <template v-slot:custom-content>
      <app-loading :loading="isLoading" is-background />

      <template v-if="!isLoading">
        <b-col>
          <!-- Top up section -->
          <sme-card large class="mb-4">
            <b-row>
              <b-col lg="7">
                <h4 class="top-up-title mb-2">Top up your reward balance</h4>
                <p class="text-muted mb-3">
                  To top up your rewards balance, generate funding instructions you can send to your finance team. Your
                  balance is credited once we receive the funds in your recognition account.
                </p>

                <p class="label-text mb-2">How this works:</p>
                <ol class="instructions-list">
                  <li>Enter the amount and a purchase order number.</li>
                  <li>We'll generate a PDF with the transfer details for your recognition account.</li>
                  <li>You send the funding instructions to your finance team.</li>
                  <li>When your transfer is received, your balance is topped up.</li>
                </ol>
              </b-col>

              <b-col lg="5">
                <ValidationObserver ref="formObserver" v-slot="v">
                  <b-form @submit.prevent="v.handleSubmit(onCreateInvoice)">
                    <app-input
                      v-model="amountInput"
                      name="Amount to add"
                      type="number"
                      :prepend="currencySymbol"
                      inputmode="decimal"
                      rules="required|min_value:1"
                      :validate-immediate="false"
                    >
                      <template #label><span class="label-text">Amount to add</span></template>
                    </app-input>

                    <app-input v-model="purchaseOrder" name="Purchase order" type="text">
                      <template #label>
                        <div class="d-flex justify-content-between w-100">
                          <span class="label-text">Add a purchase order</span>
                          <small class="text-muted">Optional</small>
                        </div>
                      </template>
                    </app-input>

                    <b-button type="submit" variant="primary" block :disabled="isCreatingInvoice" class="mt-3">
                      <b-spinner v-if="isCreatingInvoice" small class="mr-2" />
                      Generate funding instructions
                    </b-button>
                  </b-form>
                </ValidationObserver>
              </b-col>
            </b-row>
          </sme-card>

          <!-- Invoices table -->
          <sme-card large>
            <app-loading :loading="isLoadingInvoices" />

            <p v-if="!isLoadingInvoices && invoices.length === 0" class="text-muted mb-0">
              You haven't generated any funding instructions yet. Create one to get started.
            </p>

            <b-table
              v-if="!isLoadingInvoices && invoices.length > 0"
              :fields="invoiceFields"
              :items="invoices"
              table-class="table-minimal mb-0"
            >
              <template #cell(status)="{ item }">
                <span
                  class="status-badge"
                  :class="item.status === 'PAID' ? 'status-badge--paid' : 'status-badge--pending'"
                >
                  {{ item.status === 'PAID' ? 'Funded' : 'Awaiting funds' }}
                </span>
              </template>

              <template #cell(actions)="{ item }">
                <div class="d-flex align-items-center justify-content-end actions-cell">
                  <a
                    v-if="item.status === 'PENDING'"
                    v-b-tooltip.hover
                    href="#"
                    title="Delete funding instructions"
                    class="action-link"
                    @click.prevent="onDeleteInvoice(item)"
                  >
                    <font-awesome-icon :icon="['far', 'trash']" />
                  </a>
                  <a
                    v-if="item.status === 'PENDING'"
                    href="#"
                    class="action-link"
                    @click.prevent="onMarkComplete(item)"
                  >
                    <font-awesome-icon :icon="['far', 'check']" />
                    Mark as complete
                  </a>
                  <a :href="item.pdf_url" target="_blank" rel="noopener" class="action-link">
                    <font-awesome-icon :icon="['far', 'eye']" />
                    View
                  </a>
                  <a href="#" class="action-link" @click.prevent="downloadPdf(item)">
                    <font-awesome-icon :icon="['far', 'download']" />
                    Download PDF
                  </a>
                </div>
              </template>
            </b-table>
          </sme-card>
        </b-col>
      </template>
    </template>

    <!-- Footer -->
    <template v-slot:footer>
      <b-modal
        v-model="showDeleteModal"
        title="Delete funding instructions"
        ok-variant="danger"
        ok-title="Delete"
        @ok="confirmDeleteInvoice"
      >
        <p v-if="invoiceToDelete">
          Are you sure you want to delete funding instructions {{ invoiceToDelete.invoice_number }}?
        </p>
      </b-modal>

      <b-modal
        v-model="showMarkCompleteModal"
        title="Mark top-up as complete"
        size="lg"
        ok-variant="primary"
        ok-title="Mark as complete"
        :ok-disabled="!canConfirmMarkComplete"
        @ok.prevent="confirmMarkComplete"
        @hidden="resetMarkCompleteModal"
      >
        <template v-if="invoiceToMarkComplete">
          <p class="mb-3">
            Select the bank deposits that fund top-up <strong>{{ invoiceToMarkComplete.invoice_number }}</strong> ({{
              toCurrency(invoiceToMarkComplete.amount_minor)
            }}). The total of the selected deposits must match the top-up amount exactly.
          </p>

          <app-loading :loading="isLoadingUnlinkedDeposits" />

          <p v-if="!isLoadingUnlinkedDeposits && unlinkedDeposits.length === 0" class="text-muted mb-0">
            No unlinked deposits found. Once your finance team pays the invoice, the deposit will show up here.
          </p>

          <div v-if="!isLoadingUnlinkedDeposits && unlinkedDeposits.length > 0" class="deposit-list">
            <b-form-checkbox-group v-model="selectedDepositIds" stacked>
              <b-form-checkbox
                v-for="deposit in unlinkedDeposits"
                :key="deposit.recognition_ledger_deposit_id"
                :value="deposit.recognition_ledger_deposit_id"
                :class="['deposit-row', { 'deposit-row--selected': isDepositSelected(deposit) }]"
              >
                <div class="d-flex justify-content-between align-items-center w-100">
                  <div class="d-flex align-items-center">
                    <font-awesome-icon
                      v-if="isDepositSelected(deposit)"
                      :icon="['far', 'check']"
                      class="text-muted mr-2"
                    />
                    <div>
                      <div class="font-weight-bold">{{ toCurrency(deposit.amount_minor) }}</div>
                      <div class="text-muted small">
                        {{ deposit.reference || 'No reference' }}
                      </div>
                    </div>
                  </div>
                  <div class="text-muted small text-right">
                    {{ deposit.received_at ? format(new Date(deposit.received_at), 'dd MMM yyyy HH:mm') : '—' }}
                  </div>
                </div>
              </b-form-checkbox>
            </b-form-checkbox-group>
          </div>

          <div
            v-if="!isLoadingUnlinkedDeposits && unlinkedDeposits.length > 0"
            class="d-flex justify-content-between align-items-center mt-3 pt-3 border-top"
          >
            <span class="text-muted">Selected total</span>
            <span :class="['font-weight-bold', selectedSumMatches ? 'text-success' : 'text-muted']">
              {{ toCurrency(selectedSumMinor) }}
              <font-awesome-icon v-if="selectedSumMatches" :icon="['far', 'check']" class="ml-1" />
            </span>
          </div>

          <p
            v-if="!isLoadingUnlinkedDeposits && unlinkedDeposits.length > 0 && markCompleteHint"
            class="text-danger small mt-2 mb-0"
          >
            {{ markCompleteHint }}
          </p>
        </template>
      </b-modal>
    </template>
  </app-page>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useQuery, useMutation } from '@tanstack/vue-query';
import { ValidationObserver } from 'vee-validate';
import { format } from 'date-fns';

import AppPage from '@/components/AppPage.vue';
import AppStat from '@/components/AppStat.vue';
import AppLoading from '@/components/AppLoading.vue';
import AppInput from '@/components/AppInput.vue';
import SmeCard from '@/components/atoms/SmeCard.vue';

import {
  getRecognitionLedger,
  getRecognitionInvoices,
  getRecognitionUnlinkedDeposits,
  createRecognitionInvoice,
  deleteRecognitionInvoice,
  markRecognitionInvoiceComplete,
} from '@/queries/recognition';
import { getCurrencyFormatter, toCurrency } from '@/utils';
import { currencySymbolFromCode } from '@/utils/common';
import { getInvoiceDownloadHref } from '@/pages/recognition/utils/invoiceDownload';
import useToast from '@/composables/useToast';
import State from '@/state/State';

const { doToast, TOAST_VARIANT } = useToast();

// Form state
const formObserver = ref(null);
const amountInput = ref('');
const purchaseOrder = ref('');
const invoiceToDelete = ref(null);

// Mark-complete state
const showMarkCompleteModal = ref(false);
const invoiceToMarkComplete = ref(null);
const selectedDepositIds = ref([]);

// Queries
const ledgerQuery = useQuery(getRecognitionLedger());
const invoicesQuery = useQuery(getRecognitionInvoices());
const unlinkedDepositsQuery = useQuery(getRecognitionUnlinkedDeposits(() => showMarkCompleteModal.value));
const createInvoiceMutation = useMutation(createRecognitionInvoice());
const deleteInvoiceMutation = useMutation(deleteRecognitionInvoice());
const markCompleteMutation = useMutation(markRecognitionInvoiceComplete());

// Computed
const ledger = computed(() => ledgerQuery.data.value);
const invoices = computed(() => invoicesQuery.data.value || []);
const unlinkedDeposits = computed(() => unlinkedDepositsQuery.data.value || []);
const isLoading = computed(() => ledgerQuery.isPending.value);
const isLoadingInvoices = computed(() => invoicesQuery.isPending.value);
const isLoadingUnlinkedDeposits = computed(() => showMarkCompleteModal.value && unlinkedDepositsQuery.isFetching.value);
const isCreatingInvoice = computed(() => createInvoiceMutation.isPending.value);

const isDepositSelected = deposit => selectedDepositIds.value.includes(deposit.recognition_ledger_deposit_id);

const selectedSumMinor = computed(() =>
  unlinkedDeposits.value
    .filter(d => selectedDepositIds.value.includes(d.recognition_ledger_deposit_id))
    .reduce((sum, d) => sum + d.amount_minor, 0),
);
const selectedSumMatches = computed(
  () => !!invoiceToMarkComplete.value && selectedSumMinor.value === invoiceToMarkComplete.value.amount_minor,
);
const canConfirmMarkComplete = computed(() => selectedSumMatches.value && !markCompleteMutation.isPending.value);
const markCompleteHint = computed(() => {
  if (!invoiceToMarkComplete.value) {
    return '';
  }
  if (selectedDepositIds.value.length === 0) {
    return 'Select one or more deposits to enable Mark as complete.';
  }
  const delta = invoiceToMarkComplete.value.amount_minor - selectedSumMinor.value;
  if (delta > 0) {
    return `Selected total is ${toCurrency(delta)} short of the top-up amount.`;
  }
  if (delta < 0) {
    return `Selected total exceeds the top-up amount by ${toCurrency(-delta)}.`;
  }
  return '';
});

const currencySymbol = computed(() => currencySymbolFromCode(State.state?.company?.default_currency));

// Table fields
const invoiceFields = [
  { key: 'invoice_number', label: 'Reference' },
  {
    key: 'created_at',
    label: 'Created',
    formatter: value => format(new Date(value), 'dd MMM yyyy'),
  },
  {
    key: 'amount_minor',
    label: 'Amount',
    formatter: value => toCurrency(value),
  },
  {
    key: 'purchase_order',
    label: 'PO number',
    formatter: value => value || '—',
  },
  { key: 'status', label: 'Status' },
  { key: 'actions', label: '', thStyle: { width: '1px' } },
];

// Methods
const onCreateInvoice = async () => {
  const amount = parseFloat(amountInput.value);
  if (!amount || amount <= 0) {
    return;
  }

  const payload = {
    amount_minor: Math.round(amount * 100),
  };

  if (purchaseOrder.value?.trim()) {
    payload.purchase_order = purchaseOrder.value.trim();
  }

  try {
    await createInvoiceMutation.mutateAsync(payload);
    doToast('Success', 'Funding instructions generated', TOAST_VARIANT.SUCCESS);
    amountInput.value = '';
    purchaseOrder.value = '';
    requestAnimationFrame(() => formObserver.value?.reset());
  } catch {
    doToast('Error', 'Failed to generate funding instructions', TOAST_VARIANT.DANGER);
  }
};

const showDeleteModal = ref(false);

const onDeleteInvoice = invoice => {
  invoiceToDelete.value = invoice;
  showDeleteModal.value = true;
};

const confirmDeleteInvoice = async () => {
  if (!invoiceToDelete.value) {
    return;
  }

  try {
    await deleteInvoiceMutation.mutateAsync(invoiceToDelete.value.recognition_ledger_deposit_invoice_id);
    doToast('Success', 'Funding instructions deleted', TOAST_VARIANT.SUCCESS);
  } catch {
    doToast(
      'Error',
      'Failed to delete funding instructions. The transfer may have already been received.',
      TOAST_VARIANT.DANGER,
    );
  } finally {
    invoiceToDelete.value = null;
  }
};

const downloadPdf = invoice => {
  // Navigate rather than fetch: CSP connect-src blocks fetch() to the S3 URL.
  const href = getInvoiceDownloadHref(invoice);
  if (!href) {
    doToast('Error', 'Failed to download PDF', TOAST_VARIANT.DANGER);
    return;
  }
  const a = document.createElement('a');
  a.href = href;
  a.download = `${invoice.invoice_number}.pdf`;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

const onMarkComplete = invoice => {
  invoiceToMarkComplete.value = invoice;
  selectedDepositIds.value = [];
  showMarkCompleteModal.value = true;
};

const resetMarkCompleteModal = () => {
  invoiceToMarkComplete.value = null;
  selectedDepositIds.value = [];
};

const confirmMarkComplete = async () => {
  if (!invoiceToMarkComplete.value || !canConfirmMarkComplete.value) {
    return;
  }

  try {
    await markCompleteMutation.mutateAsync({
      invoiceId: invoiceToMarkComplete.value.recognition_ledger_deposit_invoice_id,
      depositIds: selectedDepositIds.value,
    });
    doToast('Success', 'Top-up marked as complete', TOAST_VARIANT.SUCCESS);
    showMarkCompleteModal.value = false;
  } catch (err) {
    const errorCode = err?.variables?.error_code;
    let message;
    if (errorCode === 'DEPOSITS_NOT_AVAILABLE') {
      message = 'One or more selected deposits are no longer available. Please refresh and try again.';
    } else if (errorCode === 'DEPOSITS_AMOUNT_MISMATCH') {
      message = 'The selected deposits do not match the top-up amount.';
    } else if (errorCode === 'INVOICE_PAID') {
      message = 'This top-up has already been marked as paid.';
    } else {
      message = 'Failed to mark top-up as complete.';
    }
    doToast('Error', message, TOAST_VARIANT.DANGER);
  }
};
</script>

<style scoped lang="scss">
:deep(.page-sub-header-wrapper) {
  align-self: flex-start;
}

.header-stat {
  height: auto;

  :deep(.app-stat__value-container) {
    width: 16rem;
    justify-content: flex-start;
  }

  :deep(.app-stat__description) {
    color: var(--palette-color-default-lighten-30);
  }
}

.top-up-title,
.label-text {
  color: var(--palette-color-default);
  font-weight: 600;
}

.instructions-list {
  padding-left: 1.25rem;
  margin-bottom: 0;

  li {
    margin-bottom: 0.25rem;
    color: var(--palette-color-default-lighten-20);
  }
}

.actions-cell {
  gap: 1.25rem;
  white-space: nowrap;
}

.status-badge {
  border-radius: 50rem;
  padding: 0.125rem 0.375rem;
  font-weight: 500;

  &--pending {
    color: var(--palette-color-warning-darken-10);
    background-color: var(--palette-color-warning-lighten-95);
    border: 1px solid var(--palette-color-warning-lighten-80);
  }

  &--paid {
    color: var(--palette-color-success-darken-10);
    background-color: var(--palette-color-success-lighten-95);
    border: 1px solid var(--palette-color-success-lighten-80);
  }
}

.action-link {
  color: var(--palette-color-brand-primary);
  text-decoration: none;
  cursor: pointer;
  border: none;
  font-weight: 400;
  display: flex;
  align-items: center;
  gap: 0.375rem;

  &:hover {
    text-decoration: underline;
  }
}

.deposit-list {
  max-height: 24rem;
  overflow-y: auto;
}

.deposit-row {
  padding: 0.5rem 0.5rem;
  border-bottom: 1px solid var(--palette-color-default-lighten-90);
  transition: background-color 0.15s ease;

  &:last-child {
    border-bottom: none;
  }

  &--selected {
    background-color: var(--palette-color-default-lighten-95);
  }

  :deep(label) {
    width: 100%;
    cursor: pointer;
  }
}
</style>
